import React, { Fragment } from "react";
import { useSelector } from "react-redux";

import { profileSlice } from "redux/features/profile";
import { useOccupationLookup } from "components/shared/occupation";
import { useInstitutionLookup } from "./institution";
import { useGeographicalLocations } from "redux/features/countries";
import { useAuthenticationState } from "redux/features/auth";
import { AFFILIATION_CHANGEABLE_FIELDS } from "./common";

/*

For the purpose of ascertaining how best to display the details of
affiliation to a particular institution, how to prompt for updated
information, and how to succinctly summarize changes, we calculate
additional details for each affiliation and for each affiliation status
record that represents a collection of affiliations paired with an occupation.

What follows is a summary of the data structures used in direct communication
with the API ("affiliation status record" received from the API, and a "change
record" sent to the API, each containing "affiliation" objects), and the
enhanced "status details record" formats we derive from them, each containing
"affiliation details" objects and embedded "location details" objects.

A note on naming conventions: fields passed directly to/from the API use
snake_case naming conventions, matching the perl internal structures on the
server. Fields used solely in the front end use normal JavaScript camelCase
conventions.

A. ORIGINAL STATUS AFFILIATION RECORD - as received from the database - has

  * date information and other fields not relevant here
  * location (representing institution-independent, "home" location)
  * statuses

  "statuses" is a list with usually exactly one entry but capable of zero
  or more. Each item is an object whose entries are:
    - date information and other fields not relevant here
    - occupation_id
    - occupation_label
    - affiliations

    "affiliations" is a list. Each item is an object whose relevant entries are:
      + institution_id
      + institution_name
      + department_name
      + location 
      + institution_location
      + approved_institution_location
      * institution_location_change_rejection
 
  Each of "location" (both at the top level and at the affiliation level) and
  "institution_location" and "approved_institution_location" is, if non-null,
  a string-valued location code (aka "symbolic id") such as "CA-ON" for
  Ontario, Canada. The "approved_institution_location" is only present
  if "institution_location" is something the person has requested staff apply
  to our database but has not yet been applied, and holds the value that's
  actually still in the database. The "institution_location_change_rejection"
  entry is only present if such a request was made but rejected, and is an
  object with subentries "request" (location value that was requested) and
  "response" (message with reason for the rejection).

B. CHANGE RECORD

Represents proposed new information to send to the database.  Differs from the
incoming original status record in that it can not specify more than one
occupational status. Entries are:

  * from/through dates if changing dates
  * location
  * occupation_id
  * affiliations   

  Each item in the "affiliations" list is an object whose entries are:
    - institution_id (which may be an existing id or the string "new")
    - new_institution_name (specified if and only if institution_id is "new")
    - department_name
    - location
    - institution_location

Any entry within a change record may be undefined, meaning "no change", except
that, in the final version sent to the server:

    "institution_id" in an item in the "affiliations" list must be specified.
    (However, the "affilations" list itself may be undefined if no
    affiliations are to be changed).

    "affiliations" and "occupation_id" can only be left undefined if the
    original affiliation status record has exactly one item in "statuses",
    in which case that item specifies the occupation and affiliations that
    will be left unchanged.

These fields may be undefined in the preliminary state of the change record,
however, before the user has selected how to populate them.

C. STATUS DETAILS RECORD

A status details record may be either an "orginal status details" record,
calculated from an original affiliation status record, or a "proposed status
details" record, calculated from a change record but using fallback to
original status details for entries that are not present in the change
record.

There are two types of entries in a status details record:

  (1) Copies of information from the underlying record (or, in the case
      of a proposed status details record, from the original status details
      if the entry isn't present in the change record), and flags related
      to the copying.

  (2) Calculated details, including an "affiliation details object"
      (described below) for each affiliation, a "location details" object
      (described below) for each location, and summary/aggregate information
      about the collection as a whole.

Status details records only contain a single occupation_id entry, a single
occupation_label entry, and a single affiliations list. Original status
records have a "statuses" list with separate occupation/affilation information
in each item, but normally it is a 1-item list and the original status
details record is prepared from that single item. 

When calculating status details from an original status record that has
multiple items in its statuses list, a "whichStatus" argument to the
calculation routine determines the behaviour. If whichStatus is defined, it
specifies an integer index into the satuses array and all information is taken
from that item. This is used when displaying summary information separately
for each status. Otherwise, occupation_id and occupation_label will be set
to null and the affiliations list will be the concatenation of the lists
from each item in statuses. The database structure ensures that no institution
is represented in more than one status item, but to safeguard against
corrupted data we also explictly verify this and filter out duplicates.

When calculating status details from an original status record that has
no items in its statuses list, occupation_id and occupation_label will
again be null and affiliations will be an empty array [].

Here are the fields in a status details record:

(1) Fields copied from the underlying record as described above:

  * location
  * occupation_id 
  * occupation_label [only present in original status details, not proposed]
  * affiliations

plus, in the case of an original status details record constructed from
an original status record whose statuses list did not have a unique
single item, one of the following boolean flags:

  * noStatuses
  * multipleStatuses

Note that, if "affiliations" is not present in the change record and is
copied instead from original status details, each item in the copied list
only has the entries appropriate for sending bsck to the server in a change
record entry; read-only fields such as "institution_name" and validity date
information are not copied.

(2) Calculated details

  * affiliationDetailsList
      a list of "affiliation details" objects for each affiliation in the
      affiliation list. The structure of an affiliation details object is
      described below.

  * affiliationDetailsIndex

      an object whose keys are the ids of those affiliation objects that have
      institutions specified (omitting blank slots that are awaiting
      institution selection), mapping the id to the affiliation details object
      itself. The format of the id is described as part of the affiliation
      details object structure description below.

  * homeLocationDetails
      a "location details" object for the location specified by the "location"
      entry, or null if no location specified. The structure of a location
      details object is described below.

  * haveLocationConflicts
      boolean, set if any affiliation details object has "conflictingArea"
      or "conflictingCountry" set.

  * haveLocations
      boolean, set if homeLocationDetails is non-null or any of the
      locationDetails fields in the affiliation details objects is non-null.

  * haveAffiliationLocations
      boolean, set if any of the locationDetails fields in the affiliation
      details objects is non-null.
 
  * singleLocation

      present if all the locations (homeLocationDetails plus the
      locationDetails fields in each affiliation details object) are the same,
      after excluding unspecified locations and locations that are whole
      countries but where some other location specifies a specific
      province/state within it. If present, is set to the first such location
      details object.

  * singleAffiliationLocation
      if there are no specified affiliation locations, is the same as
      singleLocation. Otherwise, calculated as singleLocation but only
      considering location objects within affiliation details objects, not
      homeLocationDetails. Indicates that, in the absence of any
      location/institution location conflicts, all affiliations can be
      displayed with a single location in front rather than repeating the
      location for each affililation.

  * haveMissingAreas:
      Boolean, set if there is at least one location with missingArea set
      without areaProvidedElsewhere

  * haveAmbiguousLocations:
      Boolean, set if there are multiple different locations specified but no
      home location to identify where the person actually is (haveLocations is
      set but singleLocation is not set and homeLocationDetails is an
      unspecified location details object -- i.e., has null properties)     
   
Plus, a proposed status details record will also have

  * incompleteItems
      a list of items that need completion before submission to the server
      is possible. Each item is an object with a "message" entry, and possibly
      a boolean staffCanLeaveUnresolved.

  * internalInconsistencies
      a list of affiliation items in the change record that aren't what they
      are supposed to be, either because of a program bug or failure to handle
      some unforeseen circumstances. Each item is an object with two entries:
      affiliationNumber is the index in the "affiliations" list of where
      the proble, is, corrections in an object with updated values that
      should be applied.

  * unchanged
      Boolean, set if no affiliation changes are currently pending, and the
      affiliations in the status details record have simply been copied from
      the original. Needed so the caller can tell it has to forcibly add these
      to the pending changes record in cases where affiliation submission
      is required due to resolution of no or multiple occupations.

 D. AFFILIATION DETAILS OBJECT
  
  Each affiliation details object (item in the affiliationDetailsList) has:
 
    * affiliationNumber:
        0-based index of where the affiliation is in the list

    * id, name:
        Institution id and name if existing institution, "new-N" and "N" if
        creating a new institution named "N", null if no institution selected
        yet.

     * department_name, location, institution_location:
        The corresponding entries from the item in the status record's
        "affiliations" list, as received from or sent to the API (hence
        the perl-ish snake_case naming).

    * approved_institution_location, institution_location_change_rejection
        Similarly, but present only in an original affiliation detaiol object.

    * locationDetails, institutionLocationDetails:
        A "location details" object, as described below, calculated from
        location and institution_location respectively.

    * approvedInstitutionLocationDetails:
        Present only in an original affiliation detail object where
        approved_institution_location is present, and is the corresponding
        location details object.

    * rejectedInstitutionLocationDetails:
        Present only in an original affiliation detail object where
        institution_location_change_rejected is present, and is the
        location details object corresponding to the "request" subentry.

    * conflictingCountry, conflictingArea:
        set to a non-null object when location is fully specified and
        location and institution_location are in different countries,
        or in the same country but different areas (provinces/states),
        respectively. These were implemented as objects rather than
        boolean flags because the original intent was for them to
        contain information used to prompt the user how to resolve the
        conflict. However, calculating that information has now mostly
        been implemented in the ResolveConflict component instead, so
        conflictingCountry is always an empty object and
        conflictingArea has a single entry "terminology" describing
        the terminology for areas in that country (i.e., "province" or
        "state" etc.)

  If representing proposed changes not existing data, also has or may have:

    * isUnspecified, isNew:
        Booleans set for convenience if institution not specified yet (i.e,
        id is null) or if it has been requested that a new institution be
        created

    * institution:
        Full details of the institution record as was returned by the
        server when listing institution information, except when institution
        is unspecified or new, in which case is null.

    * original:
        If the selected institution was present in the original status record,
        is a copy of the affiliation details object from there.
        If the institution is in the database but not the original status
        record, is an object with the following entries generated from
        the "institution" entry (or other status records):
            id, name, institution_location, institutionLocationDetails,
            and, if applicable, approved_institution_location and
            approvedInstitutionLocationDetails, or
            institution_location_change_rejected and
            rejectedInstitutionLocationDetails.
        Otherwise (i.e., if isNew or isUnspecified), is an empty object.

    * allowNationwideInstitution:
        Present if the user has selected a province/state as their
        location and the institution is not yet recorded in our database as
        being in that particular province/state, so the user needs to choose
        whether the institution is nationwide or in that specific province/state.
        If present, is an object with a single entry "selection" that is
        the currently proposed value of institution_location, or null if
        undefined.

    * changingInstitutionLocation:
        If the proposed changes involve a change to the institution's location
        in the database, contains a message describing what the change will
        be.

  E. LOCATION DETAILS OBJECT

  Each location details object has:

    * properties:
        the object describing the location's properties, as returned by the API
        from the server. Null if location is missing, null, or invalid/unknown.

    * missingArea:
        string, non-null if location is a country for which we require
        province/state information to be provided, and contains the terminology
        (e.g. "province or territory" for Canada, "state or territory" for the
        US) that should be used in the request for missing information.

    * isHome:
        boolean, set if location corresponds to the institution-independent
        location rather than an affiliation location, to allow separate
        treatment when processing all location details objects together in a
        list.

  Once included in a full status details record, may also have added to it:

    * areaProvidedElsewhere:
        Boolean, set if missingArea is set but another location details
        object provides the missing area,
    * areaProvidedByAnotherAffiliation:
        Boolean, set if missingArea is set and another location details
        object that does not have isHome set provides the missing area.


*/

function buildLocationDetails(location, locationLookup) {
  const properties = location === null ? null : locationLookup[location];
  const missingArea =
    properties && properties.isCountry && properties.has_fixed_area_choices
      ? properties.administrative_area_terminology || "province/state"
      : null;
  return { properties, missingArea, isHome: false };
}

function checkLocationConflict(affiliationDetails, locationLookup) {
  if (!affiliationDetails) return;
  const { locationDetails, institutionLocationDetails } = affiliationDetails;
  const affilProps = locationDetails.properties;
  const instProps = institutionLocationDetails.properties;
  if (!affilProps || !instProps) return;
  if (affilProps.symbolic_id === instProps.symbolic_id) return;
  if (locationDetails.missingArea) return;
  if (affilProps.country_id === instProps.country_id) {
    if (institutionLocationDetails.missingArea) return;
    else
      affiliationDetails.conflictingArea = {
        terminology:
          (locationLookup[affilProps.country_symbolic_id] || {})
            .administrative_area_terminology || "province/state",
      };
  } else {
    affiliationDetails.conflictingCountry = {};
  }
}

function prepareOriginalAffiliationDetails(affiliation, affiliationNumber) {
  return {
    affiliationNumber,
    id: affiliation.institution_id,
    name: affiliation.institution_name,
    department_name: affiliation.department_name,
    location: affiliation.location,
    institution_location: affiliation.institution_location,
    approved_institution_location: affiliation.approved_institution_location,
    institution_location_change_rejection:
      affiliation.institution_location_change_rejection,
  };
}

function prepareProposedAffiliationDetails(
  proposed,
  affiliationNumber,
  originalLookup,
  institutionLookup,
  affiliationRelatedInstitutionData
) {
  const d = { affiliationNumber };
  d.isNew = proposed.institution_id === "new";
  d.id = d.isNew
    ? "new-" + proposed.new_institution_name
    : proposed.institution_id ?? null;

  d.isUnspecified = d.id === null;
  const institution =
    d.isNew || d.isUnspecified ? null : institutionLookup[d.id] || null;
  const institutionProperties = institution || {};
  d.name = d.isNew
    ? proposed.new_institution_name
    : institutionProperties.name ?? null;

  d.original =
    d.isNew || d.isUnspecified
      ? {}
      : originalLookup[d.id] || {
          id: d.id,
          name: institutionProperties.name,
          institution_location: institutionProperties.location,
          ...(affiliationRelatedInstitutionData[d.id] || {}),
        };
  const updated = (key, orig, origKey = key) =>
    proposed[key] === undefined ? orig[origKey] : proposed[key];

  d.department_name = updated("department_name", d.original);
  d.location = updated("location", d.original);
  d.institution_location = updated(
    "institution_location",
    institutionProperties,
    "location"
  );
  d.institution = institution;
  return d;
}

function addStandardAffiliationDetails(details, locationLookup) {
  details.locationDetails = buildLocationDetails(
    details.location,
    locationLookup
  );
  details.institutionLocationDetails = buildLocationDetails(
    details.institution_location,
    locationLookup
  );
  if (details.approved_institution_location)
    details.approvedInstitutionLocationDetails = buildLocationDetails(
      details.approved_institution_location,
      locationLookup
    );
  if (details.institution_location_change_rejection)
    details.rejectionInstitutionLocationDetails = buildLocationDetails(
      details.institution_location_change_rejection.request,
      locationLookup
    );
  if (details.original) {
    details.original.institutionLocationDetails = buildLocationDetails(
      details.original.institution_location,
      locationLookup
    );
    if (details.original.approved_institution_location)
      details.original.approvedInstitutionLocationDetails = buildLocationDetails(
        details.original.approved_institution_location,
        locationLookup
      );
    if (details.original.institution_location_change_rejection)
      details.original.rejectedInstitutionLocationDetails = buildLocationDetails(
        details.original.institution_location_change_rejection.request,
        locationLookup
      );
  }

  checkLocationConflict(details, locationLookup);
}

function prepareStatusDetailsRecord(baseRecord, locationLookup) {
  const sd = { ...baseRecord };
  sd.homeLocationDetails = buildLocationDetails(sd.location, locationLookup);
  sd.homeLocationDetails.isHome = true;
  sd.affiliationDetailsIndex = Object.fromEntries(
    sd.affiliationDetailsList
      .filter((details) => !details.isUnspecified)
      .map((details) => [details.id, details])
  );

  sd.haveLocationConflicts =
    sd.affiliationDetailsList.filter(
      (d) => d.conflictingCountry || d.conflictingArea
    ).length > 0;

  const allLocations = [
    sd.homeLocationDetails,
    ...sd.affiliationDetailsList.map((a) => a.locationDetails),
  ].filter((locdetails) => locdetails && !!locdetails.properties);
  sd.haveLocations = allLocations.length > 0;
  sd.haveAffiliationLocations =
    allLocations.filter((l) => !l.isHome).length > 0;

  allLocations.forEach((loc) => {
    if (loc.missingArea) {
      allLocations.forEach((otherloc) => {
        if (
          otherloc.properties.country_id === otherloc.properties.countryId &&
          !otherloc.missingArea
        ) {
          loc.areaProvidedElsewhere = true;
          if (!otherloc.isHome) loc.areaProvidedByAnotherAffiliation = true;
        }
      });
    }
  });

  const independentLocations = allLocations.filter(
    (loc) => !loc.areaProvidedElsewhere
  );
  const independentAffilLocations = allLocations.filter(
    (loc) => !loc.isHome && !loc.areaProvidedByAnotherAffiliation
  );

  const first =
    independentLocations.length > 0 ? independentLocations[0] : null;

  const firstAffil =
    independentAffilLocations.length > 0 ? independentAffilLocations[0] : null;

  sd.singleLocation =
    first &&
    independentLocations.filter(
      (loc) => loc.properties.symbolic_id !== first.properties.symbolic_id
    ).length === 0
      ? first
      : null;

  if (firstAffil) {
    sd.singleAffiliationLocation =
      independentAffilLocations.filter(
        (loc) =>
          loc.properties.symbolic_id !== firstAffil.properties.symbolic_id
      ).length === 0
        ? firstAffil
        : null;
  } else sd.singleAffiliationLocation = sd.singleLocation;

  sd.haveMissingAreas =
    allLocations.filter((loc) => loc.missingArea && !loc.areaProvidedElsewhere)
      .length > 0;

  sd.haveAmbiguousLocations =
    sd.haveLocations &&
    !sd.singleLocation &&
    !sd.homeLocationDetails.properties;

  return sd;
}

export function useOriginalStatusDetails(
  originalStatusRecord = { statuses: [] },
  whichStatus = originalStatusRecord &&
  originalStatusRecord.statuses.length === 1
    ? 0
    : null
) {
  const { location, statuses } = originalStatusRecord;

  // If combining affiliations from multiple status records, verify no
  // duplicated institutions (should not happen unless data is corrupted):
  const uniqueId = (seen) => ({ id }) => (seen[id] ? false : (seen[id] = true));

  const affiliations =
    whichStatus === null
      ? statuses
          .map((s) => s.affiliations)
          .flat()
          .filter(uniqueId({}))
      : statuses[whichStatus].affiliations;

  const occupationInfo = whichStatus === null ? {} : statuses[whichStatus];
  const baseRecord = {
    location: location,
    occupation_id: occupationInfo.occupation_id,
    occupation_label: occupationInfo.occupation_label,
    noStatuses: statuses.length === 0,
    multipleStatuses: statuses.length > 1,
    affiliations,
    affiliationDetailsList: affiliations.map(prepareOriginalAffiliationDetails),
  };
  const { locationLookup } = useGeographicalLocations();

  baseRecord.affiliationDetailsList.forEach((a) =>
    addStandardAffiliationDetails(a, locationLookup)
  );

  return prepareStatusDetailsRecord(baseRecord, locationLookup);
}

// Helper routines for assessing affiliation details objects for
// proposed changes once all have been assembled, making any necessary
// adjustments and adding to the status details record's
// incompleteItems and internalInconsistencies entries if needed.

// "details" here are affiliation details.

function handleUnspecifiedInstitution(
  details,
  { affiliationDetailsList, incompleteItems },
  youAre
) {
  const { affiliationNumber } = details;
  const description =
    affiliationDetailsList.length === 1
      ? "the institution"
      : affiliationNumber === 0
      ? "the first institution"
      : affiliationNumber === 1
      ? "the second institution"
      : affiliationNumber === 2
      ? "the third institution"
      : "institution #" + (affiliationNumber + 1);
  incompleteItems.push({
    message: `Please select ${description} ${youAre} affiliated with`,
  });
}

function handleMissingLocation(
  details,
  { incompleteItems, internalInconsistencies }
) {
  const origInstLoc = details.original.institutionLocationDetails || {};

  if (!origInstLoc.properties) {
    incompleteItems.push({
      message: `Please specify the country where ${details.name} is located`,
    });
  } else {
    console.error(
      "Unexpectedly missing location for affiliation to institution " +
        `${details.name} that has a known location`
    );
    internalInconsistencies.push({
      affiliationNumber: details.affiliationNumber,
      correction: {
        location: origInstLoc.properties.symbolic_id,
        institution_location: origInstLoc.properties.symbolic_id,
      },
    });
  }
}

function handleMissingInstitutionLocation(
  details,
  { incompleteItems, internalInconsistencies }
) {
  if (!details.locationDetails.properties) return;
  const origInstLoc = details.original.institutionLocationDetails || {};
  if (origInstLoc.properties) {
    console.error(
      "Unexpectedly missing location for institution " +
        `${details.name} that has a known location`
    );
    internalInconsistencies.push({
      affiliationNumber: details.affiliationNumber,
      correction: {
        institution_location: origInstLoc.properties.symbolic_id,
      },
    });
  } else if (
    details.allowNationwideInstitution &&
    details.allowNationwideInstitution.selection === null
  ) {
    incompleteItems.push({
      message:
        `Please whether ${details.name} is nationwide or in ` +
        details.locationDetails.properties.label,
    });
  } else {
    console.error(
      `Unexpectedly missing country for institution ${details.name}` +
        ": should be the same as the affiliation location " +
        details.locationDetails.properties.full_label
    );
    internalInconsistencies.push({
      affiliationNumber: details.affiliationNumber,
      correction: {
        institution_location: details.location,
      },
    });
  }
}

function assessProposedLocationConflict(
  details,
  { incompleteItems, internalInconsistencies }
) {
  const origInstLoc = details.original.institutionLocationDetails || {};
  if (
    origInstLoc.properties &&
    origInstLoc.properties.symbolic_id === details.institution_location
  ) {
    incompleteItems.push({
      message: `Please resolve the location conflict for ${details.name}`,
    });
  } else {
    console.error(
      "Unexpected location conflict for institution " +
        `${details.name}: institution location ` +
        details.institutionLocationDetails.properties.full_label +
        " is neither currently selected location " +
        details.locationDetails.properties.full_label +
        " nor original institution location " +
        (origInstLoc.properties ? origInstLoc.properties.full_label : "(none)")
    );
    internalInconsistencies.push({
      affiliationNumber: details.affiliationNumber,
      correction: {
        institution_location: (origInstLoc.properties || {}).symbolic_id,
      },
    });
  }
}

export function useProposedStatusDetails(originalStatusDetails, changes) {
  const occupationLookup = useOccupationLookup();
  const institutionLookup = useInstitutionLookup();
  const { locationLookup } = useGeographicalLocations();

  const { data: profileData } = useSelector(profileSlice.selector);

  const { authenticatedAsStaff: amStaff } = useAuthenticationState();
  const youAre = amStaff ? "this person is" : "you are";
  const your = amStaff ? "this person's" : "your";

  const updated = (key) =>
    changes[key] === undefined ? originalStatusDetails[key] : changes[key];

  const baseRecord = {
    location: updated("location"),
    occupation_id: updated("occupation_id"),
  };
  const occupation = occupationLookup[baseRecord.occupation_id] || {};
  baseRecord.occupation_label = occupation.label;
  const copyAffiliation = (a) => {
    const copy = {};
    AFFILIATION_CHANGEABLE_FIELDS.forEach((field) => {
      if (a[field] !== undefined) copy[field] = a[field];
    });
    return copy;
  };

  const unchanged = !changes.affiliations;
  const affiliations =
    changes.affiliations ||
    originalStatusDetails.affiliations.map(copyAffiliation);

  const originalLookup = originalStatusDetails.affiliationDetailsIndex;

  /* Normally, if we add an institution to the list of affiliations for this
    profile, we can just get the institution's properties out of the
    information returned by querying the server's "institutions" api.
    However, if there is information about the institution related to this
    specific person's affiliation there, such as a request submitted to staff
    to change the institution's location, we need to see that information
    even if the affiliation isn't currently part of this status record.

    Therefore, assemble affiliation-related information about the institution
    from any other status record that provides it. */

  const affiliationRelatedInstitutionData = Object.fromEntries(
    profileData.affiliation_status_summary
      .map((record) => record.statuses)
      .flat()
      .map((status) => status.affiliations)
      .flat()
      .map((affil) => {
        const {
          approved_institution_location,
          institution_location_change_rejection,
        } = affil;
        return [
          affil.institution_id,
          {
            approved_institution_location,
            institution_location_change_rejection,
          },
        ];
      })
  );

  const affiliationDetailsList = affiliations.map((details, position) =>
    prepareProposedAffiliationDetails(
      details,
      position,
      originalLookup,
      institutionLookup,
      affiliationRelatedInstitutionData
    )
  );
  affiliationDetailsList.forEach((details) => {
    addStandardAffiliationDetails(details, locationLookup);
  });

  affiliationDetailsList.forEach((details) => {
    const loc = details.locationDetails.properties;
    const origLoc = details.original.institutionLocationDetails.properties;
    const missingArea = details.locationDetails.missingArea;
    const haveConflict = details.conflictingArea || details.conflictingCountry;
    const haveOrigSame =
      loc && origLoc && origLoc.symbolic_id === loc.symbolic_id;
    const instLoc = details.institutionLocationDetails.properties;
    if (loc && !loc.isCountry && !haveConflict && !haveOrigSame) {
      details.allowNationwideInstitution = {
        selection: instLoc ? instLoc.symbolic_id : null,
      };
    }
    if (
      loc &&
      origLoc &&
      !missingArea &&
      !haveConflict &&
      !haveOrigSame &&
      !(origLoc.isCountry && origLoc.country_id === loc.country_id)
    ) {
      details.changingInstitutionLocation =
        details.original.approved_institution_location === details.location ? (
          <Fragment>
            This will cancel the previously submitted request to change the
            institution's location to <b>{origLoc.full_label}</b>
          </Fragment>
        ) : (
          <Fragment>
            This will {amStaff ? "" : "submit a request for our staff to "}{" "}
            change the institution's location in our database, from{" "}
            <b>{origLoc.full_label}</b> to <b>{loc.full_label}</b>
          </Fragment>
        );
    }
  });

  baseRecord.affiliations = affiliations;
  baseRecord.affiliationDetailsList = affiliationDetailsList;
  const statusDetails = prepareStatusDetailsRecord(baseRecord, locationLookup);
  statusDetails.unchanged = unchanged;
  statusDetails.internalInconsistencies = [];
  const incompleteItems = (statusDetails.incompleteItems = []);
  affiliationDetailsList.forEach((details) => {
    if (details.isUnspecified) {
      handleUnspecifiedInstitution(details, statusDetails, youAre);
      return;
    }

    if (!details.locationDetails.properties)
      handleMissingLocation(details, statusDetails);

    if (!details.institutionLocationDetails.properties)
      handleMissingInstitutionLocation(details, statusDetails);

    if (details.conflictingArea || details.conflictingCountry)
      assessProposedLocationConflict(details, statusDetails);

    if (details.locationDetails.missingArea) {
      incompleteItems.push({
        message:
          "Please specify the " +
          details.locationDetails.missingArea +
          " for " +
          details.name,
      });
    }
  });
  if (statusDetails.homeLocationDetails.missingArea)
    incompleteItems.push({
      message:
        "Please specify the " +
        statusDetails.homeLocationDetails.missingArea +
        ` for ${your} place of residence`,
    });
  if (statusDetails.haveAmbiguousLocations && !statusDetails.haveMissingAreas)
    incompleteItems.push({
      message: `Please specify ${your} place of residence`,
    });

  return statusDetails;
}

function ShowLocation({
  prefix = "in ",
  suffix = "",
  className = "location",
  children,
  warning = null,
}) {
  return (
    <span className={className}>
      {" "}
      {prefix}
      {children ? <span className="location-label">{children}</span> : null}
      {warning && children ? " " : ""}
      {warning ? <span className="location-warning">{warning}</span> : null}
      {suffix}
    </span>
  );
}

function ShowAffiliationLocation({ affiliationDetails, statusDetails }) {
  const { singleAffiliationLocation, homeLocationDetails } = statusDetails;

  const locationProperties = affiliationDetails.locationDetails.properties;
  const institutionLocationProperties =
    affiliationDetails.institutionLocationDetails.properties;
  const homeLocationProperties = homeLocationDetails.properties;

  if (!locationProperties)
    return singleAffiliationLocation ? null : (
      <ShowLocation warning="(missing location information)" />
    );

  const { conflictingCountry, conflictingArea } = affiliationDetails;
  const [conflictType, conflictLabel] = conflictingCountry
    ? ["country", "full_label"]
    : conflictingArea
    ? [conflictingArea.terminology, "label"]
    : [];

  const nationwideInstitution =
    locationProperties &&
    institutionLocationProperties &&
    institutionLocationProperties.isCountry &&
    !locationProperties.isCountry &&
    !conflictType;

  const matchLocation = (l1, l2) =>
    l1.country_id === l2.country_id &&
    (l1.symbolic_id === l2.symbolic_id || l1.isCountry || l2.isCountry);

  if (conflictType) {
    const homeSeparate =
      homeLocationProperties &&
      !matchLocation(homeLocationProperties, locationProperties) &&
      !matchLocation(homeLocationProperties, institutionLocationProperties);

    return singleAffiliationLocation ? (
      <ShowLocation warning={`(conflicting ${conflictType})`}>
        {institutionLocationProperties[conflictLabel]}
      </ShowLocation>
    ) : (
      <Fragment>
        <ShowLocation>locationProperties[conflictLabel]</ShowLocation>
        <ShowLocation prefix="and " warning={`(conflicting ${conflictType})`}>
          {institutionLocationProperties.full_label}
        </ShowLocation>
        {homeSeparate ? (
          <ShowLocation prefix="remotely from ">
            {homeLocationProperties.full_label}
          </ShowLocation>
        ) : null}
      </Fragment>
    );
  }

  if (singleAffiliationLocation)
    return nationwideInstitution ? (
      <ShowLocation prefix="">(nationwide institution)</ShowLocation>
    ) : null;
  return (
    <Fragment>
      <ShowLocation
        suffix={nationwideInstitution ? " (nationwide institution)" : ""}
      >
        {locationProperties.full_label}
      </ShowLocation>
      {homeLocationProperties &&
      !matchLocation(homeLocationProperties, locationProperties) ? (
        <ShowLocation prefix="remotely from ">
          {homeLocationProperties.full_label}
        </ShowLocation>
      ) : null}
    </Fragment>
  );
}

export function ShowStatus({ statusDetails, settings }) {
  const {
    occupation_id,
    occupation_label,
    homeLocationDetails,
    affiliationDetailsList,
    haveLocations,
    haveAffiliationLocations,
    haveLocationConflicts,
    singleLocation,
    singleAffiliationLocation,
  } = statusDetails;

  const lastInstitution = affiliationDetailsList.length - 1;
  const homeLocationProperties = homeLocationDetails.properties;
  return (
    <div className="affiliation-status">
      {settings.skipOccupation ? null : (
        <span className="occupation">
          {occupation_id === null ? (
            <span className="unknown">Unknown status/role</span>
          ) : (
            occupation_label
          )}
        </span>
      )}

      {settings.skipLocation ? null : singleLocation ? (
        <ShowLocation
          prefix={
            settings.skipOccupation
              ? haveAffiliationLocations
                ? "institutional affiliation and place of residence in "
                : "place of residence "
              : "in "
          }
          warning={
            singleLocation.missingArea
              ? `(missing ${singleLocation.missingArea} - please provide)`
              : null
          }
        >
          {singleLocation.properties.full_label}
        </ShowLocation>
      ) : singleAffiliationLocation && !haveLocationConflicts ? (
        <Fragment>
          <ShowLocation
            prefix={
              settings.skipOccupation ? "institutional affiliation in " : "in "
            }
          >
            {singleAffiliationLocation.properties.full_label}
          </ShowLocation>
          <ShowLocation
            prefix="remotely from "
            warning={
              homeLocationProperties.missingArea
                ? `(missing ${homeLocationProperties.missingArea})`
                : null
            }
          >
            {homeLocationProperties.full_label}
          </ShowLocation>
        </Fragment>
      ) : !haveLocations ? (
        <ShowLocation
          warning={"missing country information - please provide"}
        />
      ) : homeLocationProperties ? null : (
        <ShowLocation
          warning={"conflicting location information - please resolve"}
        />
      )}

      {affiliationDetailsList.map((details, index) => {
        return settings.skipInstitutions ? null : settings.singleInstitution &&
          settings.singleInstitution !== details.id ? null : (
          <Fragment key={details.id}>
            {settings.singleInstitution ? null : (
              <Fragment>
                {index === 0
                  ? settings.skipOccupation && settings.skipLocation
                    ? null
                    : " at "
                  : index === lastInstitution
                  ? " and "
                  : ", "}
                {details.isUnspecified ? (
                  <span className="institution-missing">{details.name}</span>
                ) : (
                  <span className="institution">{details.name}</span>
                )}
              </Fragment>
            )}{" "}
            {(details.department_name !== null && !settings.skipDepartment) ||
            settings.showNoDepartment ? (
              <Fragment>
                {" "}
                <span className="department">
                  {settings.singleInstitution ? null : "("}
                  {details.department_name ?? (
                    <span className="no-department">
                      no department specified
                    </span>
                  )}
                  {settings.singleInstitution ? null : ")"}
                </span>
              </Fragment>
            ) : null}
            {settings.skipLocation ? null : (
              <ShowAffiliationLocation
                affiliationDetails={details}
                statusDetails={statusDetails}
              />
            )}
          </Fragment>
        );
      })}
    </div>
  );
}

export function ShowOriginalStatus({
  statusRecord,
  whichStatus,
  settings = {},
}) {
  const statusDetails = useOriginalStatusDetails(statusRecord, whichStatus);
  return <ShowStatus {...{ statusDetails, settings }} />;
}

export function ShowOriginalStatusRecord({
  statusRecord,
  isPast,
  settings = {},
}) {
  return (
    <div className="block">
      {statusRecord.isFirst && !statusRecord.isSingle ? (
        <div className="status-past-date first">
          Until {statusRecord.showThrough}:
        </div>
      ) : null}
      {isPast && statusRecord.showFrom ? (
        <div className="status-past-date">
          then from {statusRecord.showFrom}:
        </div>
      ) : null}
      {statusRecord.statuses.length === 0 ? (
        <div className="missing">Missing information</div>
      ) : null}
      {statusRecord.statuses.map((status, whichStatus) => (
        <ShowOriginalStatus
          key={whichStatus}
          {...{ statusRecord, whichStatus, settings }}
        />
      ))}
      {statusRecord.showFrom && !isPast ? (
        <div className="status-current-date">
          (beginning {statusRecord.showFrom})
        </div>
      ) : null}
    </div>
  );
}
